import React, { useEffect } from 'react';
import { useSearchParams, Link as RouterLink, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Link,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { GROUP_NAME } from '../App';
import { useMarkdownContent } from './useMarkdownContent';
import License from './License';

interface LegislationItem {
  title: string;
  description: string;
  status?: 'Active' | 'Passed' | 'Failed' | 'Pending' | 'Not Introduced' | 'N/A';
  date?: string;
  link?: string;
  license?: boolean;
}

const legislationData: LegislationItem[] = [
    {
        title: 'Accountability for Civil Rights',
        description: 'A bill to ensure companies operating within Colorado do not materially assist the federal government in violating the civil rights of Colorado residents.',
        status: 'Not Introduced',
        date: '2025-07-17',
        link: 'accountability-for-civil-rights',
        license: true
    },
    {
        title: 'Federal Authority vs. State Restrictions Fact Sheet',
        description: 'A comprehensive fact sheet outlining the implications of federal authority versus state restrictions in Colorado.',
        link: 'fact-sheet',
        license: true,
    }
//   {
//     title: 'Federal Land Transfer Act',
//     description: 'Legislation to transfer federal lands back to Colorado state control, allowing for better local management and economic development.',
//     status: 'Active',
//     date: '2025-01-15',
//     link: '#'
//   },
//   {
//     title: 'Colorado Energy Independence Bill',
//     description: 'Promoting Colorado\'s energy independence by reducing federal oversight on state energy production and distribution.',
//     status: 'Pending',
//     date: '2024-12-10',
//     link: '#'
//   },
//   {
//     title: 'State Sovereignty Resolution',
//     description: 'A resolution affirming Colorado\'s rights under the 10th Amendment to govern itself with minimal federal interference.',
//     status: 'Active',
//     date: '2024-11-20',
//     link: '#'
//   }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Passed':
      return 'info';
    case 'Failed':
      return 'error';
    case 'Pending':
      return 'warning';
    default:
      return 'default';
  }
};

export default function Legislation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const billId = searchParams.get('bill');
  const { content, loading, error } = useMarkdownContent(billId);
  const [showLicense, setShowLicense] = React.useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleBackToList = () => {
    setSearchParams({});
  };

  const handleLearnMore = (bill: LegislationItem) => {
    const billSlug = bill.link
    if (!billSlug) return;

    setShowLicense(bill.license ?? false);
    setSearchParams({ bill: billSlug });
  };

  // If a bill is selected, show the detail view
  if (billId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Back to Legislation
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {content && (
          <Box
            sx={{
              textAlign: 'left',
              maxWidth: 'none',
              '& h1': { typography: 'h3', mb: 3, textAlign: 'left' },
              '& h2': { typography: 'h4', mb: 2, mt: 4, textAlign: 'left' },
              '& h3': { typography: 'h5', mb: 2, mt: 3, textAlign: 'left' },
              '& h4': { typography: 'h6', mb: 1, mt: 2, textAlign: 'left' },
              '& p': { typography: 'body1', mb: 2, textAlign: 'left' },
              '& ul, & ol': { mb: 2, pl: 3, textAlign: 'left' },
              '& li': { mb: 1, textAlign: 'left' },
              '& blockquote': { 
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                fontStyle: 'italic',
                color: 'text.secondary',
                textAlign: 'left'
              },
              '& code': {
                backgroundColor: 'grey.100',
                padding: '2px 4px',
                borderRadius: 1,
                fontSize: '0.875rem'
              },
              '& hr': {
                border: 'none',
                height: '1px',
                backgroundColor: 'divider',
                my: 3
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2
              },
              '& th, & td': {
                textAlign: 'left',
                padding: '8px 12px',
                borderBottom: '1px solid',
                borderColor: 'divider'
              },
              '& th': {
                fontWeight: 'bold',
                backgroundColor: 'grey.50'
              }
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
            {showLicense && (
                <>
                    <hr />
                    <License />
                </>
            )}
          </Box>
        )}
      </Container>
    );
  }

  // Default view - show the cards
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" component="h1" gutterBottom>
          Current Legislation
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Track the latest legislative efforts supported by {GROUP_NAME} to reduce federal overreach in Colorado.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {legislationData.map((item, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={index}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 4,
                  transition: 'box-shadow 0.3s ease-in-out'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {item.title}
                  </Typography>
                  {item.status && (
                    <Chip 
                      label={item.status} 
                      color={getStatusColor(item.status) as any}
                      size="small"
                    />
                  )}
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {item.description}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                  {item.date ? (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.date).toLocaleDateString()}
                    </Typography>
                  ) : (
                    <Box />
                  )}
                  
                  {item.link && (
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleLearnMore(item)}
                    >
                      Learn More
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
